// ════════════════════════════════════════════════════════════════════════
//  Otto als Vektor
//
//  Nachgezeichnet nach der Figur im Lehrbuch: ein Otter. Flache Flächen, keine
//  Verläufe, keine Bildpunkte – aus demselben Grund wie beim QR-Code: bei 15 mm
//  auf dem Kärtchen so scharf wie bei 40 mm auf der Urkunde, und ein
//  Klassensatz bleibt eine Datei von wenigen Kilobyte statt eines Megabytes.
//
//  Gezeichnet in einem Quadrat von 100 × 100 mit SVG-Achsen (y nach unten);
//  `drawOtto` rechnet auf die gewünschte Kantenlänge um.
// ════════════════════════════════════════════════════════════════════════

import { PDFDocument, rgb, type PDFEmbeddedPage, type PDFPage, type RGB } from "pdf-lib";

/** Die Fellfarben. Sie gehören zu Otto, nicht zum Papier – deshalb hier. */
const FELL = rgb(0.55, 0.4, 0.27);
const FELL_DUNKEL = rgb(0.44, 0.31, 0.2);
const BAUCH = rgb(0.85, 0.76, 0.63);
const DUNKEL = rgb(0.16, 0.12, 0.1);
const HELL = rgb(1, 1, 1);

type Teil = { readonly d: string; readonly color: RGB; readonly stroke?: number };

/**
 * Die Teile von hinten nach vorn. Reihenfolge ist Absicht: der Schwanz liegt
 * hinter dem Körper, die Augen über dem Kopf.
 */
const TEILE: readonly Teil[] = [
  // Schwanz, von rechts unten nach außen
  {
    d: "M 66 88 C 82 90 93 84 95 74 C 96 68 92 64 88 65 C 85 66 84 70 86 72 C 88 75 85 80 78 81 C 73 82 68 81 64 79 Z",
    color: FELL_DUNKEL,
  },
  // Körper
  {
    d: "M 50 44 C 68 44 76 58 76 72 C 76 86 65 94 50 94 C 35 94 24 86 24 72 C 24 58 32 44 50 44 Z",
    color: FELL,
  },
  // Bauchfleck
  {
    d: "M 50 58 C 60 58 65 66 65 75 C 65 84 58 89 50 89 C 42 89 35 84 35 75 C 35 66 40 58 50 58 Z",
    color: BAUCH,
  },
  // Pfoten
  {
    d: "M 36 84 C 40 84 43 87 43 90 C 43 93 40 95 36 95 C 32 95 29 93 29 90 C 29 87 32 84 36 84 Z",
    color: FELL_DUNKEL,
  },
  {
    d: "M 64 84 C 68 84 71 87 71 90 C 71 93 68 95 64 95 C 60 95 57 93 57 90 C 57 87 60 84 64 84 Z",
    color: FELL_DUNKEL,
  },
  // Ohren: weiter außen und oben, damit sie am Kopfrand als Ohren lesbar sind
  // und nicht als Beule verschwinden.
  {
    d: "M 25 16 C 31 16 35 21 35 26 C 35 31 31 34 25 34 C 19 34 15 31 15 26 C 15 21 19 16 25 16 Z",
    color: FELL_DUNKEL,
  },
  {
    d: "M 75 16 C 81 16 85 21 85 26 C 85 31 81 34 75 34 C 69 34 65 31 65 26 C 65 21 69 16 75 16 Z",
    color: FELL_DUNKEL,
  },
  // Kopf, etwas breiter als hoch - ein Otterkopf ist keine Kugel
  {
    d: "M 50 8 C 70 8 80 21 80 36 C 80 52 67 61 50 61 C 33 61 20 52 20 36 C 20 21 30 8 50 8 Z",
    color: FELL,
  },
  // Schnauze, breit und flach
  {
    d: "M 50 38 C 63 38 70 43 70 49 C 70 56 61 60 50 60 C 39 60 30 56 30 49 C 30 43 37 38 50 38 Z",
    color: BAUCH,
  },
  // Barthaare, je drei Striche - ohne sie ist es irgendein Tier
  {
    d: "M 34 47 L 21 43 M 34 51 L 20 51 M 34 55 L 22 59",
    color: FELL_DUNKEL,
    stroke: 0.7,
  },
  {
    d: "M 66 47 L 79 43 M 66 51 L 80 51 M 66 55 L 78 59",
    color: FELL_DUNKEL,
    stroke: 0.7,
  },
  // Augen
  {
    d: "M 38 29 C 41 29 43 32 43 35 C 43 38 41 40 38 40 C 35 40 33 38 33 35 C 33 32 35 29 38 29 Z",
    color: DUNKEL,
  },
  {
    d: "M 62 29 C 65 29 67 32 67 35 C 67 38 65 40 62 40 C 59 40 57 38 57 35 C 57 32 59 29 62 29 Z",
    color: DUNKEL,
  },
  // Lichter in den Augen - sie machen den freundlichen Blick
  {
    d: "M 40 32 C 41 32 42 33 42 34 C 42 35 41 36 40 36 C 39 36 38 35 38 34 C 38 33 39 32 40 32 Z",
    color: HELL,
  },
  {
    d: "M 64 32 C 65 32 66 33 66 34 C 66 35 65 36 64 36 C 63 36 62 35 62 34 C 62 33 63 32 64 32 Z",
    color: HELL,
  },
  // Nase: klein und rund, sonst frisst sie die halbe Schnauze
  {
    d: "M 50 43 C 53 43 55 45 55 47 C 55 49 52 50 50 50 C 48 50 45 49 45 47 C 45 45 47 43 50 43 Z",
    color: DUNKEL,
  },
  // Mund: ein dünner Strich nach unten, dann zwei flache Bögen. Getrennt vom
  // Nasenpfad, sonst wächst beides zu einem Schnauzbart zusammen.
  { d: "M 50 50 L 50 53", color: DUNKEL, stroke: 0.8 },
  { d: "M 45 53 C 47 57 53 57 55 53", color: DUNKEL, stroke: 0.8 },
];

/**
 * Otto auf die Seite, `size` als Kantenlänge, `x`/`y` als linke untere Ecke.
 *
 * `drawSvgPath` zeichnet nach unten – deshalb wird oben angesetzt.
 */
export function drawOtto(
  page: PDFPage,
  box: { readonly x: number; readonly y: number; readonly size: number },
): void {
  const scale = box.size / 100;
  for (const teil of TEILE) {
    page.drawSvgPath(teil.d, {
      x: box.x,
      y: box.y + box.size,
      scale,
      ...(teil.stroke === undefined
        ? { color: teil.color }
        : { borderColor: teil.color, borderWidth: teil.stroke * scale }),
    });
  }
}

/**
 * Otto einmal gezeichnet, als Form zum Einbetten.
 *
 * Dieselbe Überlegung wie beim QR-Code: acht Kärtchen je Seite und 25 je
 * Klassensatz. Zwanzig Pfade in jede Zelle zu schreiben wäre ein Vielfaches der
 * Datei, für ein Bild, das überall dasselbe zeigt.
 */
export async function embedOtto(
  doc: PDFDocument,
  size: number,
): Promise<PDFEmbeddedPage> {
  const carrier = await PDFDocument.create();
  const page = carrier.addPage([size, size]);
  drawOtto(page, { x: 0, y: 0, size });
  return doc.embedPage(page);
}
